import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import DaskKartOdemeModal from '../pages/DaskKartOdemeModal';

const DaskYenileme = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    tc: '',
    telefon: '',
    dogumTarihi: '',
    oncekiPoliceNo: '',
    kvkk: true
  });

  const [step, setStep] = useState(1); // 1: Form, 2: Fiyatlar, 3: Satın Al
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [teklifData, setTeklifData] = useState(null);
  
  // Kart ödeme modal state'leri
  const [showKartModal, setShowKartModal] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState(null);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.tc || formData.tc.length !== 11 || !/^\d+$/.test(formData.tc)) {
      errors.tc = 'Geçerli bir TC Kimlik Numarası giriniz';
    }

    const phoneRegex = /^[0-9]{10,11}$/;
    if (!formData.telefon || !phoneRegex.test(formData.telefon.replace(/\D/g, ''))) {
      errors.telefon = 'Geçerli bir telefon numarası giriniz';
    }

    if (!formData.dogumTarihi) {
      errors.dogumTarihi = 'Doğum tarihi gereklidir';
    }

    if (!formData.oncekiPoliceNo) {
      errors.oncekiPoliceNo = 'Önceki poliçe numarası gereklidir';
    }

    if (!formData.kvkk) {
      errors.kvkk = 'KVKK metnini kabul etmelisiniz';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      return;
    }
  
    setLoading(true);

    try {
      const response = await fetch(`${window.location.protocol}//${window.location.host}/dask-yenileme-teklif.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          daskTipi: 'yenileme'
        })
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.message || 'Bir hata oluştu');
      }
  
      setTeklifData(data);
      setStep(2);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePolicyPurchase = async (selectedPrice, paymentType = 'havale') => {
    if (paymentType === 'kart') {
      // Kart ödeme modal'ını aç
      setSelectedPrice(selectedPrice);
      setShowKartModal(true);
      return;
    }

    // Havale/EFT ile direkt poliçeleştirme
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${window.location.protocol}//${window.location.host}/dask-policekayit.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          teklifKodu: teklifData.teklifKodu,
          secilenTaksit: selectedPrice.taksit_sayisi
        })
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.message || 'Poliçeleştirme sırasında hata oluştu');
      }
  
      // Başarılı poliçeleştirme
      navigate(`/dask-basarili?policeNo=${data.policeNo}&teklifKodu=${teklifData.teklifKodu}`);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  return (
    <div className="overflow-x-hidden">
      <Helmet>
        <title>DASK Yenileme - Zorunlu Deprem Sigortası | SigortamOlsun</title>
        <meta name="description" content="DASK poliçenizi kolayca yenileyin. Önceki poliçe numaranız ile hızlı teklif alın." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href="https://sigortamolsun.com/dask-yenileme" />
      </Helmet>

      <div className="px-4 py-8">
        <div className="max-w-2xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              DASK Yenileme
            </h1>
            <p className="text-gray-600">
              Mevcut DASK poliçenizi kolayca yenileyin
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${step >= 1 ? 'bg-blue-600' : 'bg-gray-300'}`}>
                1
              </div>
              <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`}>
                2
              </div>
              <div className={`w-12 h-0.5 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'}`}>
                3
              </div>
            </div>
          </div>

          {/* Step 1: Form */}
          {step === 1 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold mb-6">DASK Yenileme Bilgileri</h2>
              
              {/* Bilgilendirme */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-blue-400 text-xl">ℹ️</span>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      DASK Yenileme İşlemi
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>Önceki poliçe numaranız ile bina bilgileriniz otomatik olarak gelecektir. Sadece kişisel bilgilerinizi doğrulayın ve teklif alın.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                
                {/* Kişisel Bilgiler */}
                <div className="border-b pb-4 mb-4">
                  <h3 className="font-medium text-gray-700 mb-3">Kişisel Bilgiler</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        TC Kimlik Numaranız *
                      </label>
                      <input
                        type="tel"
                        placeholder="TC Kimlik Numarası"
                        className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600
                          ${validationErrors.tc ? 'border-red-500' : 'border-gray-300'}`}
                        maxLength="11"
                        value={formData.tc}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          setFormData({ ...formData, tc: value });
                          if (validationErrors.tc) {
                            setValidationErrors({ ...validationErrors, tc: '' });
                          }
                        }}
                      />
                      {validationErrors.tc && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.tc}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Telefon Numaranız *
                      </label>
                      <input
                        type="tel"
                        placeholder="Telefon Numarası"
                        className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600
                          ${validationErrors.telefon ? 'border-red-500' : 'border-gray-300'}`}
                        value={formData.telefon}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          setFormData({ ...formData, telefon: value });
                          if (validationErrors.telefon) {
                            setValidationErrors({ ...validationErrors, telefon: '' });
                          }
                        }}
                      />
                      {validationErrors.telefon && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.telefon}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Doğum Tarihi *
                      </label>
                      <input
                        type="date"
                        className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600
                          ${validationErrors.dogumTarihi ? 'border-red-500' : 'border-gray-300'}`}
                        value={formData.dogumTarihi}
                        onChange={(e) => {
                          setFormData({ ...formData, dogumTarihi: e.target.value });
                          if (validationErrors.dogumTarihi) {
                            setValidationErrors({ ...validationErrors, dogumTarihi: '' });
                          }
                        }}
                      />
                      {validationErrors.dogumTarihi && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.dogumTarihi}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Önceki Poliçe Numarası *
                      </label>
                      <input
                        type="text"
                        placeholder="Önceki DASK poliçe numarası"
                        className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600
                          ${validationErrors.oncekiPoliceNo ? 'border-red-500' : 'border-gray-300'}`}
                        value={formData.oncekiPoliceNo}
                        onChange={(e) => {
                          setFormData({ ...formData, oncekiPoliceNo: e.target.value });
                          if (validationErrors.oncekiPoliceNo) {
                            setValidationErrors({ ...validationErrors, oncekiPoliceNo: '' });
                          }
                        }}
                      />
                      {validationErrors.oncekiPoliceNo && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.oncekiPoliceNo}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Önceki DASK poliçenizin numarasını giriniz
                      </p>
                    </div>
                  </div>
                </div>

                {/* KVKK */}
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={formData.kvkk}
                    onChange={(e) => {
                      setFormData({ ...formData, kvkk: e.target.checked });
                      if (validationErrors.kvkk) {
                        setValidationErrors({ ...validationErrors, kvkk: '' });
                      }
                    }}
                    className={`mt-1 ${validationErrors.kvkk ? 'border-red-500' : ''}`}
                  />
                  <label className="text-sm text-gray-600">
                    <Link to="/kvkk" className="text-blue-600 hover:text-blue-700">KVKK</Link>
                    {' ve '}
                    <Link to="/aydinlatma-metni" className="text-blue-600 hover:text-blue-700">site kullanım koşullarını</Link>
                    {' kabul ediyorum'}
                  </label>
                </div>
                {validationErrors.kvkk && (
                  <p className="text-sm text-red-600">{validationErrors.kvkk}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full bg-blue-600 text-white py-3 rounded-lg transition font-medium
                    ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                >
                  {loading ? 'Teklif Alınıyor...' : 'DASK Yenileme Teklifi Al'}
                </button>
              </form>

              {/* Yardım Bilgisi */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Poliçe Numaranızı Bulamıyor Musunuz?</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Önceki DASK poliçe numaranızı aşağıdaki yerlerden bulabilirsiniz:
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Önceki poliçe belgesi üzerinde</li>
                  <li>• Sigorta şirketinizden alınan SMS/email bildirimlerde</li>
                  <li>• Önceki acentenizden</li>
                  <li>• TSB (Türkiye Sigorta Birliği) e-bordro sisteminden</li>
                </ul>
                <p className="text-sm text-blue-600 mt-2">
                  Yardıma ihtiyacınız varsa: <strong>0850 XXX XX XX</strong>
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Fiyat Seçimi */}
          {step === 2 && teklifData && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">DASK Yenileme Fiyat Seçenekleri</h2>
                <button
                  onClick={() => setStep(1)}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                  ← Geri Dön
                </button>
              </div>
              
              {/* Poliçe Bilgileri */}
              {teklifData.policeDetay && (
                <div className="mb-6 p-4 bg-green-50 rounded-lg">
                  <h3 className="font-medium text-green-900 mb-2">Önceki Poliçe Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div className="text-green-700">
                      <span className="font-medium">Adres:</span> {teklifData.policeDetay.adres}
                    </div>
                    <div className="text-green-700">
                      <span className="font-medium">Metrekare:</span> {teklifData.policeDetay.metrekare} m²
                    </div>
                    <div className="text-green-700">
                      <span className="font-medium">Önceki Prim:</span> {formatPrice(teklifData.policeDetay.oncekiPrim)}
                    </div>
                    <div className="text-green-700">
                      <span className="font-medium">Bitiş Tarihi:</span> {formatDate(teklifData.policeDetay.bitisTarihi)}
                    </div>
                  </div>
                </div>
              )}

              {/* Teminat Bilgileri */}
              {teklifData.teminatlar && teklifData.teminatlar.length > 0 && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-medium text-blue-900 mb-2">Teminat Detayları</h3>
                  {teklifData.teminatlar.map((teminat, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-blue-700">{teminat.ad}</span>
                      <span className="font-medium text-blue-900">
                        {formatPrice(teminat.bedel)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Fiyat Seçenekleri */}
              <div className="space-y-4">
                {teklifData.fiyatlar.map((fiyat, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium text-gray-800">{fiyat.taksit_tipi}</h3>
                        <p className="text-sm text-gray-600">
                          {fiyat.taksit_sayisi} taksit
                        </p>
                        {fiyat.odeme_plani && fiyat.odeme_plani.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500">Ödeme Planı:</p>
                            {fiyat.odeme_plani.slice(0, 3).map((odeme, idx) => (
                              <div key={idx} className="text-xs text-gray-600">
                                {formatDate(odeme.vade)}: {formatPrice(odeme.tutar)}
                              </div>
                            ))}
                            {fiyat.odeme_plani.length > 3 && (
                              <div className="text-xs text-gray-500">
                                ... ve {fiyat.odeme_plani.length - 3} taksit daha
                              </div>
                            )}
                          </div>
                        )}
                        {/* İndirim varsa göster */}
                        {teklifData.policeDetay?.oncekiPrim && fiyat.brut_prim < teklifData.policeDetay.oncekiPrim && (
                          <div className="mt-1">
                            <span className="text-green-600 text-sm font-medium">
                              {formatPrice(teklifData.policeDetay.oncekiPrim - fiyat.brut_prim)} tasarruf!
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600">
                          {formatPrice(fiyat.brut_prim)}
                        </div>
                        {teklifData.policeDetay?.oncekiPrim && (
                          <div className="text-sm text-gray-500 line-through">
                            {formatPrice(teklifData.policeDetay.oncekiPrim)}
                          </div>
                        )}
                        <div className="mt-2 space-y-2">
                          <button 
                            className="w-full bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 transition disabled:opacity-50"
                            disabled={loading}
                            onClick={() => handlePolicyPurchase(fiyat, 'havale')}
                          >
                            {loading ? 'İşleniyor...' : 'Havale/EFT ile Yenile'}
                          </button>
                          <button 
                            className="w-full bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition disabled:opacity-50"
                            disabled={loading}
                            onClick={() => handlePolicyPurchase(fiyat, 'kart')}
                          >
                            Kart ile Yenile
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Ek Bilgiler */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
                <p><strong>Teklif Kodu:</strong> {teklifData.teklifKodu}</p>
                <p className="mt-1">Fiyatlar KDV dahildir. Teklif 30 gün geçerlidir.</p>
                <p className="mt-1">Hasarsızlık indirimi uygulanmıştır.</p>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Kart Ödeme Modal */}
      <DaskKartOdemeModal
        isOpen={showKartModal}
        onClose={() => setShowKartModal(false)}
        selectedPrice={selectedPrice}
        teklifKodu={teklifData?.teklifKodu}
        onPaymentComplete={(result) => {
          setShowKartModal(false);
          if (result.success) {
            navigate(`/dask-basarili?policeNo=${result.policeNo}&teklifKodu=${teklifData.teklifKodu}&tip=kart`);
          } else {
            setError(result.message);
          }
        }}
      />
    </div>
  );
};

export default DaskYenileme;